export interface FacehashProfileImageInput {
  actorId?: string | null;
  displayName?: string | null;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  fallbackSeed?: string;
}

function normalizeSeed(value: string | null | undefined): string {
  return typeof value === 'string' ? value.trim() : '';
}

function normalizeProfileImageUrl(value: string | null | undefined): string {
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

function hashSeed(value: string): number {
  let hash = 0;

  for (const character of value) {
    hash = (hash * 31 + character.charCodeAt(0)) >>> 0;
  }

  return hash;
}

const defaultProfileImagePalette = [
  'hsl(12 62% 84%)',
  'hsl(28 68% 84%)',
  'hsl(46 72% 82%)',
  'hsl(82 48% 82%)',
  'hsl(116 42% 84%)',
  'hsl(156 44% 82%)',
  'hsl(186 54% 84%)',
  'hsl(214 60% 84%)',
  'hsl(246 58% 86%)',
  'hsl(276 54% 86%)',
  'hsl(314 52% 84%)',
] as const;

export function buildDefaultProfileImageName(
  input: FacehashProfileImageInput,
): string {
  const firstName = normalizeSeed(input.firstName);
  const lastName = normalizeSeed(input.lastName);

  if (firstName.length > 0 || lastName.length > 0) {
    return [firstName, lastName].filter(Boolean).join(' ');
  }

  const displayName = normalizeSeed(input.displayName);
  if (displayName.length > 0) {
    return displayName;
  }

  const email = normalizeSeed(input.email).toLowerCase();
  if (email.length > 0) {
    return email;
  }

  const actorId = normalizeSeed(input.actorId);
  if (actorId.length > 0) {
    return `actor:${actorId}`;
  }

  return normalizeSeed(input.fallbackSeed) || 'tov-plus-profile';
}

export function buildFacehashProfileImageUrl(seed: string): string {
  const normalizedSeed = normalizeSeed(seed) || 'tov-plus-profile';

  return `https://www.facehash.dev/api/avatar?name=${encodeURIComponent(normalizedSeed)}`;
}

export function buildDefaultProfileImageUrl(
  input: FacehashProfileImageInput,
): string {
  return buildFacehashProfileImageUrl(buildDefaultProfileImageName(input));
}

export function buildDefaultProfileImageColor(
  input: FacehashProfileImageInput,
): string {
  const seed = buildDefaultProfileImageName(input);
  const hash = hashSeed(seed);

  return defaultProfileImagePalette[hash % defaultProfileImagePalette.length]!;
}

export function isLegacyFacehashProfileImageUrl(
  value: string | null | undefined,
): boolean {
  const normalizedValue = normalizeProfileImageUrl(value);

  return (
    normalizedValue.startsWith('https://www.facehash.dev/api/avatar?') ||
    normalizedValue.startsWith('http://www.facehash.dev/api/avatar?') ||
    normalizedValue.startsWith('https://facehash.dev/api/avatar?') ||
    normalizedValue.startsWith('http://facehash.dev/api/avatar?')
  );
}
